import { create } from 'zustand';
import { useTicketStore, Ticket, TicketAttachment, TicketHistoryItem } from './ticketStore';
import { useLeaveStore, LeaveRequest } from './leaveStore';
import { useAssetStore, Asset, AssetRequest, MaintenanceJob } from './assetStore';
import { useHolidayStore } from './holidayStore';
import { useShiftStore } from './shiftStore';
import { useAuthStore } from './authStore';
import { apiClient } from '../lib/api';
import toast from 'react-hot-toast';

export type OriginModule = 'people' | 'assets' | 'support' | 'money' | 'work' | 'growth' | 'admin';
export type EntryChannel = 'portal' | 'chat' | 'module' | 'ai_assistant' | 'email' | 'phone' | 'manual';
export type DepartmentQueue = 'hr' | 'assets' | 'support' | 'finance' | 'pmo' | 'growth' | 'admin';
export type RequestRecordType = 
  | 'leave_request'
  | 'wfh_request'
  | 'attendance_regularization'
  | 'shift_swap'
  | 'holiday_request'
  | 'payroll_query'
  | 'document_request'
  | 'policy_enquiry'
  | 'onboarding_case'
  | 'offboarding_case'
  | 'training_request'
  | 'headcount_requisition'
  | 'asset_requisition'
  | 'asset_fault'
  | 'asset_return'
  | 'asset_transfer'
  | 'general_it';

export interface AuditAction {
  id: string;
  timestamp: string;
  action: string;
  sourceView: 'module' | 'ticket' | 'portal' | 'chat' | 'system';
  originModule: OriginModule;
  recordId: string | number;
  ticketId: string | number;
  performedBy: string;
  details: string;
}

export interface UnifiedRequestPayload {
  originModule: OriginModule;
  recordType: RequestRecordType;
  ticketTypeId?: string;
  title: string;
  description: string;
  priority?: 'low' | 'medium' | 'high' | 'urgent';
  urgency?: 'low' | 'medium' | 'high' | 'urgent';
  impact?: 'low' | 'medium' | 'high' | 'urgent';
  entryChannel?: EntryChannel;
  requesterId?: string;
  requesterName?: string;
  requesterEmail?: string;
  requesterDepartment?: string;
  raisedById?: string;
  raisedByName?: string;
  departmentQueue?: DepartmentQueue;
  assignedAssetId?: string;
  assignedAssetCode?: string;
  assignedAssetName?: string;
  isConfidential?: boolean;
  metadata?: Record<string, any>;
  parentTicketId?: string | number;
  childTasks?: Array<{
    title: string;
    departmentQueue: DepartmentQueue;
    assigneeRole?: string;
  }>;
}

// -------------------------------------------------------------
// STATUS MAPPINGS
// -------------------------------------------------------------

export const STATUS_MAPPINGS: Record<RequestRecordType, {
  moduleToTicket: Record<string, string>;
  ticketToModule: Record<string, string>;
}> = {
  leave_request: {
    moduleToTicket: {
      pending: 'awaiting-approval',
      approved: 'resolved',
      rejected: 'closed',
      cancelled: 'closed',
    },
    ticketToModule: {
      'new': 'pending',
      'open': 'pending',
      'awaiting-approval': 'pending',
      'in-progress': 'pending',
      'resolved': 'approved',
      'closed': 'rejected',
    },
  },
  wfh_request: {
    moduleToTicket: {
      pending: 'awaiting-approval',
      approved: 'resolved',
      rejected: 'closed',
      cancelled: 'closed',
    },
    ticketToModule: {
      'new': 'pending',
      'open': 'pending',
      'awaiting-approval': 'pending',
      'in-progress': 'pending',
      'resolved': 'approved',
      'closed': 'rejected',
    },
  },
  attendance_regularization: {
    moduleToTicket: {
      pending: 'open',
      approved: 'resolved',
      rejected: 'closed',
      cancelled: 'closed',
    },
    ticketToModule: {
      'new': 'pending',
      'open': 'pending',
      'in-progress': 'pending',
      'resolved': 'approved',
      'closed': 'rejected',
    },
  },
  shift_swap: {
    moduleToTicket: {
      pending: 'open',
      approved: 'resolved',
      rejected: 'closed',
    },
    ticketToModule: {
      'new': 'pending',
      'open': 'pending',
      'resolved': 'approved',
      'closed': 'rejected',
    },
  },
  holiday_request: {
    moduleToTicket: {
      pending: 'open',
      approved: 'resolved',
      rejected: 'closed',
    },
    ticketToModule: {
      'new': 'pending',
      'open': 'pending',
      'resolved': 'approved',
      'closed': 'rejected',
    },
  },
  payroll_query: {
    moduleToTicket: {
      submitted: 'open',
      in_review: 'in-progress',
      resolved: 'resolved',
      closed: 'closed',
    },
    ticketToModule: {
      'new': 'submitted',
      'open': 'submitted',
      'in-progress': 'in_review',
      'resolved': 'resolved',
      'closed': 'closed',
    },
  },
  document_request: {
    moduleToTicket: {
      pending: 'open',
      in_progress: 'in-progress',
      completed: 'resolved',
      rejected: 'closed',
    },
    ticketToModule: {
      'new': 'pending',
      'open': 'pending',
      'in-progress': 'in_progress',
      'resolved': 'completed',
      'closed': 'rejected',
    },
  },
  policy_enquiry: {
    moduleToTicket: {
      open: 'open',
      answered: 'resolved',
      closed: 'closed',
    },
    ticketToModule: {
      'open': 'open',
      'resolved': 'answered',
      'closed': 'closed',
    },
  },
  onboarding_case: {
    moduleToTicket: {
      pending: 'open',
      in_progress: 'in-progress',
      completed: 'resolved',
      rejected: 'closed',
    },
    ticketToModule: {
      'new': 'pending',
      'open': 'pending',
      'in-progress': 'in_progress',
      'resolved': 'completed',
      'closed': 'rejected',
    },
  },
  offboarding_case: {
    moduleToTicket: {
      initiated: 'open',
      in_progress: 'in-progress',
      completed: 'resolved',
    },
    ticketToModule: {
      'new': 'initiated',
      'open': 'initiated',
      'in-progress': 'in_progress',
      'resolved': 'completed',
      'closed': 'completed',
    },
  },
  training_request: {
    moduleToTicket: {
      pending: 'open',
      approved: 'resolved',
      rejected: 'closed',
    },
    ticketToModule: {
      'new': 'pending',
      'open': 'pending',
      'resolved': 'approved',
      'closed': 'rejected',
    },
  },
  headcount_requisition: {
    moduleToTicket: {
      pending_approval: 'awaiting-approval',
      approved: 'resolved',
      rejected: 'closed',
    },
    ticketToModule: {
      'awaiting-approval': 'pending_approval',
      'resolved': 'approved',
      'closed': 'rejected',
    },
  },
  asset_requisition: {
    moduleToTicket: {
      pending: 'awaiting-approval',
      approved: 'in-progress',
      fulfilled: 'resolved',
      rejected: 'closed',
    },
    ticketToModule: {
      'awaiting-approval': 'pending',
      'in-progress': 'approved',
      'resolved': 'fulfilled',
      'closed': 'rejected',
    },
  },
  asset_fault: {
    moduleToTicket: {
      reported: 'open',
      scheduled: 'open',
      in_progress: 'in-progress',
      completed: 'resolved',
      cancelled: 'closed',
    },
    ticketToModule: {
      'new': 'reported',
      'open': 'scheduled',
      'in-progress': 'in_progress',
      'resolved': 'completed',
      'closed': 'cancelled',
    },
  },
  asset_return: {
    moduleToTicket: {
      requested: 'open',
      inspected: 'in-progress',
      returned: 'resolved',
    },
    ticketToModule: {
      'new': 'requested',
      'open': 'requested',
      'in-progress': 'inspected',
      'resolved': 'returned',
    },
  },
  asset_transfer: {
    moduleToTicket: {
      requested: 'awaiting-approval',
      transferred: 'resolved',
      rejected: 'closed',
    },
    ticketToModule: {
      'awaiting-approval': 'requested',
      'resolved': 'transferred',
      'closed': 'rejected',
    },
  },
  general_it: {
    moduleToTicket: {
      open: 'open',
      resolved: 'resolved',
      closed: 'closed',
    },
    ticketToModule: {
      'open': 'open',
      'resolved': 'resolved',
      'closed': 'closed',
    },
  },
};

// -------------------------------------------------------------
// SLA ENGINE CALCULATOR
// -------------------------------------------------------------

export function calculateSLADueDates(priority: string = 'medium', startDate: Date = new Date()) {
  const normPriority = (priority || 'medium').toLowerCase();
  
  // Standard target durations in minutes
  let responseMin = 240; // 4 hours
  let resolutionMin = 1440; // 24 hours

  switch (normPriority) {
    case 'urgent':
      responseMin = 30; // 30 mins
      resolutionMin = 240; // 4 hours
      break;
    case 'high':
      responseMin = 60; // 1 hour
      resolutionMin = 480; // 8 hours
      break;
    case 'medium':
      responseMin = 240; // 4 hours
      resolutionMin = 1440; // 24 hours
      break;
    case 'low':
      responseMin = 480; // 8 hours
      resolutionMin = 2880; // 48 hours
      break;
  }

  // Calculate with working hours (default 9 AM - 6 PM Monday to Friday)
  // For simplicity and resilience, project forward in ms
  const firstResponseDue = new Date(startDate.getTime() + responseMin * 60 * 1000);
  const resolutionDue = new Date(startDate.getTime() + resolutionMin * 60 * 1000);

  return {
    slaStart: startDate.toISOString(),
    firstResponseDueAt: firstResponseDue.toISOString(),
    resolutionDueAt: resolutionDue.toISOString(),
    responseTargetMin: responseMin,
    resolutionTargetMin: resolutionMin,
  };
}

export function evaluateSLAStatus(ticket: Ticket): {
  state: 'within_sla' | 'warning' | 'breached' | 'met' | 'paused';
  timeLeftMinutes: number;
  pctElapsed: number;
  label: string;
} {
  const now = new Date().getTime();
  const isTerminal = ['resolved', 'closed', 'fulfilled'].includes(ticket.status?.toLowerCase());
  
  if (isTerminal) {
    const isMet = !ticket.resolutionDueAt || (ticket.resolvedAt ? new Date(ticket.resolvedAt).getTime() <= new Date(ticket.resolutionDueAt).getTime() : true);
    return {
      state: isMet ? 'met' : 'breached',
      timeLeftMinutes: 0,
      pctElapsed: 100,
      label: isMet ? 'SLA Met' : 'SLA Breached',
    };
  }

  const isPaused = ticket.slaHold || ['pending', 'on-hold', 'with-user', 'with-supplier', 'awaiting-approval'].includes(ticket.status?.toLowerCase());
  if (isPaused) {
    return {
      state: 'paused',
      timeLeftMinutes: 0,
      pctElapsed: 50,
      label: 'SLA Paused (Waiting for user)',
    };
  }

  if (!ticket.resolutionDueAt) {
    return {
      state: 'within_sla',
      timeLeftMinutes: 9999,
      pctElapsed: 0,
      label: 'Within SLA',
    };
  }

  const startTime = ticket.createdAt ? new Date(ticket.createdAt).getTime() : now;
  const dueTime = new Date(ticket.resolutionDueAt).getTime();
  const totalDuration = Math.max(1, dueTime - startTime);
  const elapsed = Math.max(0, now - startTime);
  const timeLeft = dueTime - now;
  const pctElapsed = Math.min(100, Math.round((elapsed / totalDuration) * 100));
  const timeLeftMinutes = Math.round(timeLeft / (60 * 1000));

  if (timeLeft <= 0) {
    return {
      state: 'breached',
      timeLeftMinutes: Math.abs(timeLeftMinutes),
      pctElapsed: 100,
      label: `Breached by ${Math.abs(timeLeftMinutes)}m`,
    };
  }

  if (pctElapsed >= 75) {
    return {
      state: 'warning',
      timeLeftMinutes,
      pctElapsed,
      label: `${Math.round(timeLeft / (60 * 1000))}m left (Warning)`,
    };
  }

  return {
    state: 'within_sla',
    timeLeftMinutes,
    pctElapsed,
    label: `${Math.round(timeLeft / (60 * 1000))}m left`,
  };
}

// -------------------------------------------------------------
// STORE DEFINITION
// -------------------------------------------------------------

interface UnifiedSyncState {
  auditLogs: AuditAction[];
  syncInProgress: Set<string>;
  
  // Create unified pair
  createUnifiedRequest: (payload: UnifiedRequestPayload) => Promise<{
    ticket: Ticket;
    moduleRecordId?: string | number;
  }>;

  // Bidirectional sync
  syncModuleActionToTicket: (params: {
    recordType: RequestRecordType;
    recordId: string | number;
    action: string;
    moduleStatus: string;
    note?: string;
    performedBy?: string;
    actorRole?: string;
  }) => Promise<void>;

  syncTicketActionToModule: (params: {
    ticketId: string | number;
    ticketStatus: string;
    resolutionNote?: string;
    performedBy?: string;
  }) => Promise<void>;

  // Asset auto-link helper
  getUserAssets: (employeeId?: string) => Promise<Asset[]>;

  // Audit recording
  logAuditAction: (action: Omit<AuditAction, 'id' | 'timestamp'>) => void;
}

export const useUnifiedSyncStore = create<UnifiedSyncState>((set, get) => ({
  auditLogs: [],
  syncInProgress: new Set<string>(),

  logAuditAction: (action) => {
    const newLog: AuditAction = {
      ...action,
      id: `audit-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`,
      timestamp: new Date().toISOString(),
    };
    set((state) => ({
      auditLogs: [newLog, ...state.auditLogs.slice(0, 199)],
    }));
  },

  getUserAssets: async (employeeId?: string) => {
    const { assets, fetchAssets } = useAssetStore.getState();
    if (!assets || assets.length === 0) {
      await fetchAssets();
    }
    const currentAssets = useAssetStore.getState().assets;
    if (!employeeId) return currentAssets;
    return currentAssets.filter((a) => a.assignedTo === employeeId || a.assignedToEmail);
  },

  createUnifiedRequest: async (payload: UnifiedRequestPayload) => {
    const { originModule, recordType, title, description, priority = 'medium', entryChannel = 'portal' } = payload;
    
    // 1. Resolve Requester & Raised By
    const authUser = useAuthStore.getState().user;
    const requesterId = payload.requesterId || authUser?.id || '00000000-0000-0000-0000-000000000001';
    const requesterName = payload.requesterName || authUser?.name || 'Authorized Requester';
    const requesterEmail = payload.requesterEmail || authUser?.email || 'user@company.local';
    const raisedById = payload.raisedById || authUser?.id;
    const raisedByName = payload.raisedByName || authUser?.name;

    // 2. Resolve Department Queue
    // Per requirement: asset module tickets are logged to Support team instead of asset/it queue.
    let queue: DepartmentQueue = 'support';
    if (originModule === 'assets') {
      queue = 'support';
    } else if (payload.departmentQueue && payload.departmentQueue !== 'assets') {
      queue = payload.departmentQueue;
    } else if (originModule === 'people') {
      queue = recordType === 'payroll_query' ? 'finance' : 'hr';
    } else if (originModule === 'money') {
      queue = 'finance';
    } else if (originModule === 'work') {
      queue = 'pmo';
    } else if (originModule === 'admin') {
      queue = 'admin';
    }

    // 3. Compute SLA Due Dates
    const slaDates = calculateSLADueDates(priority);

    // 4. Map ticket type:
    // Per requirement: All tickets generated from system across asset, request tab, and people module (leave, wfh, etc.)
    // MUST be generated as Service Request (SD_SR).
    let ticketType = 'service-request';
    if (originModule === 'assets' || originModule === 'people') {
      ticketType = 'service-request';
    } else if (
      recordType === 'leave_request' ||
      recordType === 'wfh_request' ||
      recordType === 'asset_requisition' ||
      recordType === 'asset_fault' ||
      recordType === 'asset_return' ||
      recordType === 'asset_transfer' ||
      recordType === 'general_it' ||
      recordType === 'document_request' ||
      recordType === 'training_request' ||
      recordType === 'policy_enquiry'
    ) {
      ticketType = 'service-request';
    } else if (payload.ticketTypeId) {
      ticketType = payload.ticketTypeId;
    }

    // 5. Generate initial record ID
    const moduleRecordId = `${recordType}-${Date.now()}`;

    // 6. Create Support Ticket
    const ticketStore = useTicketStore.getState();
    const createdTicket = await ticketStore.createTicket({
      subject: title,
      description: `${description}\n\n[System Context]\nOrigin Module: ${originModule.toUpperCase()}\nRecord Type: ${recordType}\nRecord ID: ${moduleRecordId}\nEntry Channel: ${entryChannel.toUpperCase()}`,
      priority,
      status: 'open',
      type: ticketType,
      ticketTypeId: ticketType,
      teamId: queue === 'support' ? 'support' : (queue === 'hr' ? 'hr' : queue),
      teamKey: queue === 'support' ? 'support' : (queue === 'hr' ? 'hr' : queue),
      source: 'System Generated',
      createdFrom: entryChannel,
      requesterId,
      requesterName,
      requesterEmail,
      requesterDepartment: payload.requesterDepartment || 'General',
      department: payload.requesterDepartment || 'General',
      createdBy: raisedByName || requesterName,
      // Metadata fields
      site: 'Headquarters',
      assetId: payload.assignedAssetId,
      assetTag: payload.assignedAssetCode,
      ...slaDates,
    });

    // Decorate ticket with unified linkage
    createdTicket.originModule = originModule;
    createdTicket.originRecordId = String(moduleRecordId);
    createdTicket.originRecordType = recordType;
    createdTicket.entryChannel = entryChannel;
    createdTicket.raisedById = raisedById;
    createdTicket.raisedByName = raisedByName;
    createdTicket.departmentQueue = queue;
    createdTicket.isConfidential = Boolean(payload.isConfidential || recordType === 'payroll_query');

    // 7. Auto-Create Module Record if Applicable
    if (recordType === 'asset_fault' && payload.assignedAssetId && entryChannel !== 'module' && originModule !== 'assets') {
      // Set asset status to "Under Maintenance"
      const assetStore = useAssetStore.getState();
      try {
        await assetStore.createMaintenance({
          assetId: payload.assignedAssetId,
          maintenance_type: 'Repair',
          description: `Fault logged via ticket ${createdTicket.ticketId}: ${title}`,
          scheduled_date: new Date().toISOString().slice(0, 10),
        });
        toast.success(`Asset marked Under repair, Ticket ${createdTicket.ticketId} opened`);
      } catch (e) {
        console.warn('Asset maintenance linkage fallback:', e);
      }
    } else if (recordType === 'asset_requisition') {
      // Only create module record if request did not originate from the asset module itself
      if (entryChannel !== 'module' && originModule !== 'assets') {
        const assetStore = useAssetStore.getState();
        try {
          await assetStore.createRequest({
            asset_type: payload.assignedAssetName || title,
            priority: priority as any,
            reason: description,
          });
        } catch (e) {
          console.warn('Asset request linkage fallback:', e);
        }
      }
    }

    // 8. Child Tasks Workflow (e.g. for Onboarding or Offboarding)
    if (payload.childTasks && payload.childTasks.length > 0) {
      const childTicketIds: (string | number)[] = [];
      for (const task of payload.childTasks) {
        const childTicket = await ticketStore.createTicket({
          subject: `[Task] ${task.title} (Parent: ${createdTicket.ticketId})`,
          description: `Sub-task for ${title}.\nParent Ticket: ${createdTicket.ticketId}`,
          priority: 'high',
          status: 'open',
          type: 'service-request',
          source: 'System Generated',
          parentId: createdTicket.id,
          requesterId,
          requesterName,
          requesterEmail,
        });
        childTicket.departmentQueue = task.departmentQueue;
        childTicket.parentId = createdTicket.id;
        childTicketIds.push(childTicket.id);
      }
      createdTicket.childTicketIds = childTicketIds;
    }

    // 9. Audit Logging
    get().logAuditAction({
      action: 'CREATE_UNIFIED_REQUEST',
      sourceView: entryChannel === 'portal' ? 'portal' : 'module',
      originModule,
      recordId: moduleRecordId,
      ticketId: createdTicket.id,
      performedBy: raisedByName || requesterName,
      details: `Created linked request pair (${recordType} + ${createdTicket.ticketId}) via channel ${entryChannel}`,
    });

    // 10. In-app notification is now produced server-side (real per-user
    // /auth/notifications), so there is no local notification-store write here.

    return { ticket: createdTicket, moduleRecordId };
  },

  syncModuleActionToTicket: async ({ recordType, recordId, action, moduleStatus, note, performedBy }) => {
    const syncLockKey = `mod-to-tkt-${recordId}-${action}`;
    const { syncInProgress } = get();
    if (syncInProgress.has(syncLockKey)) return; // Anti-loop guard

    syncInProgress.add(syncLockKey);
    try {
      const ticketStore = useTicketStore.getState();
      const targetTicketStatus = STATUS_MAPPINGS[recordType]?.moduleToTicket[moduleStatus] || 'open';

      // Find matching ticket by originRecordId or search
      const ticket = ticketStore.tickets.find(
        (t) => String(t.originRecordId) === String(recordId) || t.description?.includes(String(recordId))
      );

      if (ticket) {
        await ticketStore.updateTicket(ticket.id, {
          status: targetTicketStatus,
          resolution: action.toLowerCase().includes('approve') || action.toLowerCase().includes('complete') ? (note || 'Approved / Fulfilled in module') : undefined,
          resolvedAt: targetTicketStatus === 'resolved' ? new Date().toISOString() : undefined,
          closedAt: targetTicketStatus === 'closed' ? new Date().toISOString() : undefined,
        });

        if (note) {
          await ticketStore.addComment(ticket.id, `[Module Action: ${action.toUpperCase()}] ${note}`);
        }

        get().logAuditAction({
          action: `SYNC_MODULE_TO_TICKET_${action.toUpperCase()}`,
          sourceView: 'module',
          originModule: (ticket.originModule as OriginModule) || 'people',
          recordId,
          ticketId: ticket.id,
          performedBy: performedBy || 'System Agent',
          details: `Updated linked ticket ${ticket.ticketId} status to ${targetTicketStatus}`,
        });
      }
    } finally {
      setTimeout(() => syncInProgress.delete(syncLockKey), 500);
    }
  },

  syncTicketActionToModule: async ({ ticketId, ticketStatus, resolutionNote, performedBy }) => {
    const syncLockKey = `tkt-to-mod-${ticketId}-${ticketStatus}`;
    const { syncInProgress } = get();
    if (syncInProgress.has(syncLockKey)) return; // Anti-loop guard

    syncInProgress.add(syncLockKey);
    try {
      const ticketStore = useTicketStore.getState();
      const ticket = ticketStore.tickets.find((t) => String(t.id) === String(ticketId));
      if (!ticket || !ticket.originRecordType) return;

      const recordType = ticket.originRecordType as RequestRecordType;
      const targetModuleStatus = STATUS_MAPPINGS[recordType]?.ticketToModule[ticketStatus.toLowerCase()];
      if (!targetModuleStatus) return;

      // Execute domain-specific action
      if (recordType === 'leave_request' && ticket.originRecordId) {
        const leaveStore = useLeaveStore.getState();
        if (targetModuleStatus === 'approved') {
          await leaveStore.approveLeaveRequest(ticket.originRecordId);
        } else if (targetModuleStatus === 'rejected') {
          await leaveStore.rejectLeaveRequest(ticket.originRecordId, resolutionNote);
        }
      } else if (recordType === 'asset_fault' && ticket.assetId) {
        const assetStore = useAssetStore.getState();
        if (ticketStatus === 'resolved') {
          // Fault resolved -> return asset to In use
          await assetStore.updateAsset(String(ticket.assetId), { status: 'Assigned' });
          toast.success('Asset status restored to Assigned (In use)');
        }
      }

      get().logAuditAction({
        action: `SYNC_TICKET_TO_MODULE_${ticketStatus.toUpperCase()}`,
        sourceView: 'ticket',
        originModule: (ticket.originModule as OriginModule) || 'support',
        recordId: ticket.originRecordId || '',
        ticketId,
        performedBy: performedBy || 'Support Agent',
        details: `Updated linked domain module ${ticket.originModule} to ${targetModuleStatus}`,
      });
    } finally {
      setTimeout(() => syncInProgress.delete(syncLockKey), 500);
    }
  },
}));
